import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getFirestore, doc, getDoc, collection, getDocs, updateDoc, arrayUnion } from 'firebase/firestore';
import { FaClock, FaDollarSign, FaTasks, FaCheckCircle, FaSpinner, FaCalendarAlt, FaUserTie, FaArrowLeft, FaMapMarkerAlt, FaTimes, FaPlus, FaUser, FaStickyNote } from 'react-icons/fa';
import { FaCheck } from 'react-icons/fa';
import { toast } from 'react-toastify';

interface ProjectData {
  id: string;
  businessId: string;
  name: string;
  description: string;
  status: string;
  startDate: any;
  endDate: any;
  totalAmount: number;
  progress: number;
  tasks: any[];
  clientName?: string;
  businessName?: string;
  clientId?: string;
  materials?: string[];
  notes?: string[];
  location?: {
    address: string;
    city: string;
    state: string;
    country: string;
    zipCode: string;
  };
}

const WorkerProjectView: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { currentUser, userData } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [project, setProject] = useState<ProjectData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [taskPayments, setTaskPayments] = useState<Record<string, number>>({});
  const [activeTab, setActiveTab] = useState('tasks');
  const [notes, setNotes] = useState<any[]>([]);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (projectId) {
      fetchProjectDetails();
    }
  }, [currentUser, userData, projectId, navigate]);

  const fetchProjectDetails = async () => {
    if (!projectId || !currentUser || !userData) return;
    try {
      setLoading(true);
      setError(null);
      const db = getFirestore();
      let businessId = null;
      if (Array.isArray(userData.tareasAsignadas)) {
        const tarea = userData.tareasAsignadas.find((t: any) => t.projectId === projectId);
        if (tarea) businessId = tarea.businessId;
      }
      if (!businessId && Array.isArray(userData.businessesAsignados) && userData.businessesAsignados.length > 0) {
        businessId = userData.businessesAsignados[0];
      }
      console.log('[WorkerProjectView] businessId:', businessId, 'projectId:', projectId);
      if (!businessId) {
        setError('No tienes acceso a este proyecto');
        setLoading(false);
        return;
      }
      const businessDoc = await getDoc(doc(db, 'businesses', businessId));
      const businessData = businessDoc.exists() ? businessDoc.data() : {};
      const projectDoc = await getDoc(doc(db, `projects/${businessId}/projects`, projectId));
      if (projectDoc.exists()) {
        const projectData = projectDoc.data() as any;
        let myTasks: any[] = [];
        
        // Siempre leer desde la subcolección tasks (nueva estructura)
        const tasksRef = collection(db, `projects/${businessId}/projects/${projectId}/tasks`);
        const tasksSnap = await getDocs(tasksRef);
        const allTasks = tasksSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        console.log('[WorkerProjectView] allTasks from subcollection:', allTasks);
        
        myTasks = allTasks.filter((task: any) => {
          if (!currentUser) return false;
          // Verificar assignedTo
          if (task.assignedTo === currentUser.uid) {
            return true;
          }
          // Verificar worker (puede ser string o array)
          if (task.worker) {
            if (Array.isArray(task.worker)) {
              return task.worker.includes(currentUser.uid);
            } else if (typeof task.worker === 'string') {
              return task.worker === currentUser.uid;
            }
          }
          return false;
        });
        console.log('[WorkerProjectView] myTasks filtered:', myTasks);
        // Calcular progreso
        const myTasksProgress = myTasks.length > 0 
          ? myTasks.reduce((total: number, task: any) => {
              return total + (task.progress || 0);
            }, 0) / myTasks.length
          : 0;
        setProject({
          id: projectDoc.id,
          businessId,
          name: projectData.name,
          description: projectData.description || '',
          status: projectData.status || 'active',
          startDate: projectData.startDate,
          endDate: projectData.endDate,
          totalAmount: projectData.totalAmount || 0,
          progress: Math.round(myTasksProgress),
          tasks: myTasks,
          clientName: projectData.clientName,
          businessName: businessData.name || businessData.businessName,
          clientId: projectData.clientId,
          materials: projectData.materials,
          notes: projectData.notes,
          location: projectData.location
        });

        // Cargar notas del proyecto
        if (Array.isArray(projectData.notes)) {
          setNotes([...projectData.notes].sort((a, b) => b.timestamp - a.timestamp));
        } else {
          setNotes([]);
        }
        // Obtener pagos de cada tarea desde el wallet
        const walletRef = collection(db, `wallet/${currentUser.uid}/transacciones`);
        const walletSnap = await getDocs(walletRef);
        console.log('[WorkerProjectView] walletSnap.docs.length:', walletSnap.docs.length);
        const payments: Record<string, number> = {};
        walletSnap.docs.forEach(docSnap => {
          const trx = docSnap.data();
          if (trx.projectId === projectId && trx.taskId) {
            payments[trx.taskId] = trx.total || 0;
          }
        });
        setTaskPayments(payments);
      } else {
        setError('Proyecto no encontrado');
      }
    } catch (error) {
      console.error('Error al obtener detalles del proyecto:', error);
      setError('Error al cargar el proyecto');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return t('workerProjects.status.active');
      case 'completed': return t('workerProjects.status.completed');
      case 'pending': return t('workerProjects.status.pending');
      default: return status;
    }
  };

  const getTaskStatusColor = (columnId: string) => {
    switch (columnId) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'done': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTaskStatusText = (columnId: string) => {
    switch (columnId) {
      case 'new': return t('workerProjects.taskStatus.new');
      case 'in_progress': return t('workerProjects.taskStatus.inProgress');
      case 'done': return t('workerProjects.taskStatus.done');
      default: return columnId;
    }
  };

  const formatDate = (date: any) => {
    if (!date) return '-';
    if (date.seconds) {
      return new Date(date.seconds * 1000).toLocaleDateString(i18n.language);
    }
    return new Date(date).toLocaleDateString(i18n.language);
  };

  const formatDateTime = (date: any, time?: string) => {
    if (!date) return '-';
    const dateStr = formatDate(date);
    return time ? `${dateStr} ${time}` : dateStr;
  };

  // Función para agregar una nueva nota
  const handleAddNote = async () => {
    if (!newNote.trim() || !currentUser || !project) return;
    setSavingNote(true);
    const noteObj = {
      text: newNote.trim(),
      user: currentUser.displayName || currentUser.email || 'Usuario',
      uid: currentUser.uid,
      timestamp: Date.now()
    };
    try {
      const projectRef = doc(getFirestore(), `projects/${project.businessId}/projects/${projectId}`);
      await updateDoc(projectRef, {
        notes: arrayUnion(noteObj)
      });
      setNotes(prev => [noteObj, ...prev]);
      setNewNote('');
      toast.success(t('projects.notes.noteAdded', 'Nota agregada'));
    } catch (e) {
      console.error('Error al guardar la nota:', e);
      toast.error(t('projects.notes.errorSaving', 'Error al guardar la nota'));
    } finally {
      setSavingNote(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-8">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-red-600 mx-auto mb-4" />
            <p className="text-gray-600">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-8 text-center">
            <FaTimes className="text-6xl text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-700 mb-2">Error</h2>
            <p className="text-gray-500 mb-6">{error}</p>
            <button
              onClick={() => navigate('/worker-projects')}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors duration-200"
            >
              Volver a mis proyectos
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/worker-projects')}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 p-3 rounded-xl transition-colors duration-200"
              >
                <FaArrowLeft className="text-xl" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{project.name}</h1>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                    {getStatusText(project.status)}
                  </span>
                  {project.businessName && (
                    <span className="flex items-center gap-1">
                      <FaUserTie className="text-red-500" />
                      {project.businessName}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Descripción */}
          {project.description && (
            <p className="text-gray-600 mb-6 text-lg">{project.description}</p>
          )}

          {/* Información del proyecto */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-100 text-sm">{t('workerProjectView.myTasks')}</p>
                  <p className="text-3xl font-bold">{project.tasks.length}</p>
                </div>
                <FaTasks className="text-4xl opacity-80" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">{t('workerProjectView.myProgress')}</p>
                  <p className="text-3xl font-bold">{project.progress}%</p>
                </div>
                <FaCheckCircle className="text-4xl opacity-80" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">{t('workerProjectView.myPayment')}</p>
                  <p className="text-3xl font-bold">
                    ${project.tasks.reduce((sum, task) => sum + (task.finalTotal || 0), 0).toLocaleString()}
                  </p>
                </div>
                <FaDollarSign className="text-4xl opacity-80" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">{t('workerProjectView.duration')}</p>
                  <p className="text-3xl font-bold">
                    {project.startDate && project.endDate ? 
                      Math.ceil((new Date(project.endDate.seconds * 1000).getTime() - new Date(project.startDate.seconds * 1000).getTime()) / (1000 * 60 * 60 * 24)) : 
                      '-'} {t('workerProjectView.days')}
                  </p>
                </div>
                <FaClock className="text-4xl opacity-80" />
              </div>
            </div>
          </div>

          {/* Fechas y ubicación */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FaCalendarAlt className="text-red-500" />
                {t('workerProjectView.projectDates')}
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{t('workerProjectView.startDate')}:</span>
                  <span className="font-medium">{formatDate(project.startDate)}</span>
                </div>
                {project.endDate && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">{t('workerProjectView.endDate')}:</span>
                    <span className="font-medium">{formatDate(project.endDate)}</span>
                  </div>
                )}
              </div>
            </div>

            {project.location && (
              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FaMapMarkerAlt className="text-red-500" />
                  {t('workerProjectView.location')}
                </h3>
                <div className="space-y-2">
                  <p className="text-gray-800">{project.location.address}</p>
                  <p className="text-gray-600">
                    {project.location.city}, {project.location.state} {project.location.zipCode}
                  </p>
                  <p className="text-gray-600">{project.location.country}</p>
                </div>
              </div>
            )}
          </div>

          {/* Progreso general */}
          <div className="mb-8">
            <div className="flex items-center justify-between text-lg mb-4">
              <span className="font-semibold text-gray-900">{t('workerProjectView.generalProgress')}</span>
              <span className="font-bold text-red-600 text-xl">{project.progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div 
                className="bg-gradient-to-r from-red-500 to-pink-500 h-4 rounded-full transition-all duration-300"
                style={{ width: `${project.progress}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Mis tareas */}
        <div className="bg-white rounded-3xl shadow-2xl p-8">
          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-6">
            <button
              onClick={() => setActiveTab('tasks')}
              className={`px-6 py-3 font-semibold text-sm transition-colors duration-200 flex items-center gap-2 ${
                activeTab === 'tasks'
                  ? 'border-b-2 border-red-500 text-red-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <FaTasks className="text-sm" />
              {t('workerProjectView.tabs.tasks', 'Mis Tareas')}
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className={`px-6 py-3 font-semibold text-sm transition-colors duration-200 flex items-center gap-2 ${
                activeTab === 'notes'
                  ? 'border-b-2 border-red-500 text-red-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <FaStickyNote className="text-sm" />
              {t('workerProjectView.tabs.notes', 'Notas')}
            </button>
          </div>

          {/* Tab Content: Tasks */}
          {activeTab === 'tasks' && (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('workerProjectView.myTasksInProject')}</h2>
              
              {project.tasks.length === 0 ? (
                <div className="text-center py-8">
                  <FaTasks className="text-6xl text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No tienes tareas asignadas en este proyecto, pero puedes ver la información general.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {project.tasks.map((task) => (
                    <div 
                      key={task.id} 
                      className="bg-gray-50 rounded-2xl p-6 border border-gray-100 cursor-pointer hover:shadow-lg transition-all duration-200"
                      onClick={() => navigate(`/worker-projects/${project.id}/tasks/${task.id}`)}
                      tabIndex={0}
                      role="button"
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate(`/worker-projects/${project.id}/tasks/${task.id}`); }}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{task.title}</h3>
                          <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTaskStatusColor(task.columnId)}`}>
                              {getTaskStatusText(task.columnId)}
                            </span>
                            {task.startDate && (
                              <span className="flex items-center gap-1">
                                <FaCalendarAlt className="text-red-500" />
                                {formatDateTime(task.startDate, task.startTime)}
                              </span>
                            )}
                            {task.duration && (
                              <span className="flex items-center gap-1">
                                <FaClock className="text-red-500" />
                                {task.duration} {task.durationUnit === 'days' ? 'días' : 'horas'}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-green-600">
                            ${taskPayments[task.id]?.toLocaleString() ?? '0'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {task.finalPrice && `${task.finalPrice} ${task.finalPriceUnit === 'hour' ? '/hora' : '/día'}`}
                          </div>
                        </div>
                      </div>

                      {task.description && (
                        <p className="text-gray-700 mb-4">{task.description}</p>
                      )}

                      {/* Progreso de la tarea */}
                      {task.checklistEnabled && task.checklist && task.checklist.length > 0 && (
                        <div className="mb-4">
                          <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-gray-600">{t('workerProjectView.checklistProgress')}</span>
                            <span className="font-semibold text-red-600">{task.progress || 0}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div 
                              className="bg-gradient-to-r from-red-500 to-pink-500 h-3 rounded-full transition-all duration-300"
                              style={{ width: `${task.progress || 0}%` }}
                            ></div>
                          </div>
                        </div>
                      )}

                      {/* Ubicación de la tarea */}
                      {task.location && (
                        <div className="mb-4">
                          <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                            <FaMapMarkerAlt className="text-red-500" />
                            {t('workerProjectView.taskLocation')}
                          </h4>
                          <p className="text-gray-700">{task.location.address}</p>
                        </div>
                      )}

                      {/* Checklist */}
                      {task.checklistEnabled && task.checklist && task.checklist.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3">{t('workerProjectView.checklist')}</h4>
                          <div className="space-y-2">
                            {task.checklist.map((item: any, index: number) => (
                              <div key={index} className="flex items-center gap-3">
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                  item.completed ? 'bg-green-500 border-green-500' : 'border-gray-300'
                                }`}>
                                  {item.completed && <FaCheck className="text-white text-xs" />}
                                </div>
                                <span className={`text-sm ${item.completed ? 'line-through text-gray-500' : 'text-gray-700'}`}>
                                  {item.text}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Tab Content: Notes */}
          {activeTab === 'notes' && (
            <>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{t('workerProjectView.tabs.notes', 'Notas del Proyecto')}</h2>
              </div>

              {/* Agregar nueva nota */}
              <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('projects.notes.writeNote', 'Escribir una nota')}</h3>
                <div className="space-y-4">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder={t('projects.notes.placeholder', 'Escribe tu nota aquí...')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                    rows={4}
                  />
                  <div className="flex justify-end">
                    <button
                      onClick={handleAddNote}
                      disabled={savingNote || !newNote.trim()}
                      className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                    >
                      {savingNote ? (
                        <>
                          <FaSpinner className="animate-spin text-sm" />
                          {t('projects.notes.saving', 'Guardando...')}
                        </>
                      ) : (
                        <>
                          <FaPlus className="text-sm" />
                          {t('projects.notes.addNote', 'Agregar Nota')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Lista de notas */}
              <div className="space-y-4">
                {notes.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                    <FaStickyNote className="text-4xl text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">{t('projects.notes.noNotes', 'No hay notas en este proyecto')}</p>
                  </div>
                ) : (
                  notes.map((note, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center gap-3 mb-2">
                        {/* Avatar del usuario */}
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                          <FaUser className="text-gray-400 text-sm" />
                        </div>
                        {/* Información del usuario */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-red-600">{note.user}</span>
                          </div>
                        </div>
                        <span className="text-xs text-gray-500">{new Date(note.timestamp).toLocaleString()}</span>
                      </div>
                      <div className="text-gray-900 whitespace-pre-line">{note.text}</div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkerProjectView; 